import styled from "@emotion/styled";
import {
  COLOR_APP_TEXT,
  COLOR_GREEN_BORDER,
  COLOR_GREEN_CHECKBOX_HOVER,
  COLOR_LIGHT_BLUE_CHECKBOX_HOVER,
  MOBILE_SCREEN,
  COLOR_TERTIARY,
  COLOR_INPUT_DEFAULT_BORDER,
  BORDER_RADIUS_DEFAULT,
  COLOR_INPUT_BORDER_HOVER,
} from "../theme";
import { Paragraph } from "../typography/Paragraph";

const StyledCheckboxContainer = styled.div<{ isChecked: boolean }>`
  display: grid;
  grid-template-columns: 32px 1fr;
  align-items: start; // Align to top, then manually position checkbox to first line center
  padding: 24px;
  gap: 8px;
  margin: 4px 0;
  user-select: none;
  border-radius: ${BORDER_RADIUS_DEFAULT};
  border: ${({ isChecked }) =>
    isChecked
      ? `1px solid ${COLOR_GREEN_BORDER}`
      : `1px solid ${COLOR_INPUT_DEFAULT_BORDER}`};
  background-color: ${({ isChecked }) =>
    isChecked ? COLOR_GREEN_CHECKBOX_HOVER : "transparent"};
  &:hover {
    background-color: ${({ isChecked }) =>
      isChecked ? COLOR_GREEN_CHECKBOX_HOVER : COLOR_LIGHT_BLUE_CHECKBOX_HOVER};
    input {
      border: 1px solid ${COLOR_APP_TEXT};
    }
    border: ${({ isChecked }) =>
      isChecked
        ? `1px solid ${COLOR_GREEN_BORDER}`
        : `1px solid ${COLOR_INPUT_BORDER_HOVER}`};
    cursor: pointer;
  }
  ${MOBILE_SCREEN} {
    padding: 8px;
  }
`;

// Custom Checkbox with custom tick
const StyledCheckbox = styled.input`
  /* Remove the native checkbox appearance */
  -webkit-appearance: none;
  appearance: none;

  /* Size and basic styling */
  width: 24px;
  height: 24px;
  border: 1px solid ${COLOR_INPUT_DEFAULT_BORDER}; /* Outer circle border */
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  position: relative;
  margin-top: 0px; // Fine-tune alignment with text baseline in first line

  &:hover {
    border-color: ${COLOR_INPUT_BORDER_HOVER}; /* Outer circle border */
  }

  &:checked {
    background-color: ${COLOR_APP_TEXT};
    border-color: ${COLOR_APP_TEXT};
  }

  /* Custom tick – only shown if the checkbox is checked */
  &:checked::after {
    content: "";
    position: absolute;
    /* Adjust these values to position the tick */
    top: 4px;
    left: 8px;
    width: 4px; /* The tick's width */
    height: 10px; /* The tick's height */
    border: solid ${COLOR_TERTIARY}; /* Tick color */
    /* Change these border widths to control tick thickness */
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

export interface CheckboxProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  isChecked: boolean;
  id: string;
  onToggle: () => void;
  disabled?: boolean;
}

const Checkbox: React.FC<CheckboxProps> = ({
  isChecked,
  label,
  id,
  disabled,
  onToggle,
  ...props
}: CheckboxProps) => {
  return (
    <StyledCheckboxContainer
      isChecked={isChecked}
      onClick={onToggle}
      {...props}
    >
      <StyledCheckbox
        type="checkbox"
        checked={isChecked}
        // This is to prevent a double click when clicking the checkbox
        onChange={() => null}
        id={id}
        aria-label={label}
        data-testid="checkbox"
        disabled={disabled}
      />
      <label
        // This is to prevent the default behavior of triggering the onchange
        // for the checkbox while maintaining an accessible label
        // But allow links within the label to work normally
        onClick={(e) => {
          // Check if the click target or any parent element is a link
          const isLink =
            e.target instanceof HTMLElement &&
            (e.target.closest("a") || e.target.tagName === "A"); // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
          if (!isLink) {
            e.preventDefault();
          } else {
            // Stop event propagation to prevent checkbox toggle when clicking links
            e.stopPropagation();
          }
        }}
        htmlFor={id}
      >
        <Paragraph fontWeight={100} color={COLOR_APP_TEXT}>
          <span dangerouslySetInnerHTML={{ __html: label }} />
        </Paragraph>
      </label>
    </StyledCheckboxContainer>
  );
};

export default Checkbox;
